import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
// REAL QUESTIONS - 16 total
const QUESTIONS = [{
  id: 1,
  category: 'Conflict',
  text: 'When someone strongly disagrees with you in a meeting, you usually:',
  options: ["Push your point until it's understood", 'Try to find a middle ground', 'Step back and reassess', 'Let it go to avoid friction']
}, {
  id: 2,
  category: 'Authority',
  text: 'When a senior person makes a decision you think is wrong, you usually:',
  options: ['Challenge it directly', 'Try to influence them privately', 'Go along and adapt', 'Disengage and focus on your own scope']
}, {
  id: 3,
  category: 'Ambiguity',
  text: 'When a problem is unclear and underdefined, you usually:',
  options: ['Push for clarity before moving', 'Start anyway and adjust as you go', 'Look for alignment first', 'Wait until direction becomes clearer']
}, {
  id: 4,
  category: 'Ownership',
  text: 'When something important fails in your area, you usually:',
  options: ['Feel personally responsible and try to fix it', 'Look for what in the system failed', 'Focus on protecting the team', 'Move on to the next thing quickly']
}, {
  id: 5,
  category: 'Speed vs Quality',
  text: 'Under time pressure, you usually:',
  options: ['Cut scope to preserve quality', 'Cut quality to preserve speed', 'Try to do both and stretch', 'Escalate and renegotiate timelines']
}, {
  id: 6,
  category: 'Control',
  text: "In group work, you're most comfortable when:",
  options: ['You can guide most decisions', 'Responsibilities are clearly split', 'People fully own their parts', "You're only involved at key checkpoints"]
}, {
  id: 7,
  category: 'Harmony vs Truth',
  text: 'When you think a conversation might create tension, you usually:',
  options: ['Say what you think anyway', 'Try to phrase it carefully', 'Delay or soften the message', 'Avoid the topic unless necessary']
}, {
  id: 8,
  category: 'Stress',
  text: 'When things get tense or political, you usually:',
  options: ['Become more forceful and direct', 'Become more careful and strategic', 'Withdraw and focus on your own work', 'Try to smooth things over']
}, {
  id: 9,
  category: 'Recognition',
  text: "When your work isn't recognized, you usually:",
  options: ['Push to make it visible', 'Feel frustrated but stay quiet', 'Reframe and focus on the work', 'Start disengaging over time']
}, {
  id: 10,
  category: 'Risk',
  text: 'When a decision has real downside risk, you usually:',
  options: ['Push for the bold move', 'Look for a safer middle path', 'Try to reduce uncertainty first', 'Avoid committing until forced']
}, {
  id: 11,
  category: 'Initiative',
  text: 'When you see something broken outside your role, you usually:',
  options: ['Step in and try to fix it', 'Flag it and look for an owner', 'Ignore it and focus on your scope', 'Wait until it becomes a real problem']
}, {
  id: 12,
  category: 'Persistence',
  text: 'When you keep hitting resistance, you usually:',
  options: ['Push harder', 'Change approach', 'Step back and wait', 'Drop it and move on']
}, {
  id: 13,
  category: 'Planning',
  text: 'When starting something new, you usually:',
  options: ['Plan thoroughly before acting', 'Start quickly and iterate', 'Align people first', 'Wait for clearer direction']
}, {
  id: 14,
  category: 'Social vs Task',
  text: 'In group settings, you usually focus more on:',
  options: ['Driving the outcome', 'Keeping alignment', 'Making sure people feel okay', 'Just doing your part well']
}, {
  id: 15,
  category: 'Energy',
  text: "When you're overloaded, you usually:",
  options: ['Power through', 'Reprioritize ruthlessly', 'Slow down and conserve energy', 'Start dropping commitments']
}, {
  id: 16,
  category: 'Failure',
  text: 'After a visible failure, you usually:',
  options: ['Try to quickly prove yourself again', 'Analyze what went wrong', 'Become more cautious', 'Emotionally detach and move on']
}];
export function WorkMirrorDiagnostic() {
  const navigate = useNavigate();
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const totalQuestions = QUESTIONS.length;
  const question = QUESTIONS[currentQuestion];
  const handleAnswer = (optionIndex: number) => {
    // Save answer
    const newAnswers = {
      ...answers,
      [question.id]: optionIndex
    };
    setAnswers(newAnswers);
    // Move to next question or finish
    if (currentQuestion < totalQuestions - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      // Store answers in sessionStorage for report generation
      sessionStorage.setItem('workMirrorAnswers', JSON.stringify(newAnswers));
      // Navigate to generating screen
      navigate('/generating-report?product=work');
    }
  };
  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };
  return <>
      <Helmet data-id="element-4051">
        <title data-id="element-4052">Work Mirror Diagnostic | Lazy Monk Labs</title>
        <meta name="robots" content="noindex, nofollow" data-id="element-4053" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex flex-col" data-id="element-4054">
        {/* TOP - Progress & Helper */}
        <div className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A] border-b border-[#2B2B2B]" data-id="element-4055">
          <div className="max-w-4xl mx-auto px-6 py-6" data-id="element-4056">
            {/* Progress Indicator */}
            <div className="flex items-center justify-between mb-4" data-id="element-4057">
              <p className="text-xs text-gray-600 font-mono" data-id="element-4058">
                Question {currentQuestion + 1} of {totalQuestions}
              </p>

              {/* Back button */}
              {currentQuestion > 0 && <button onClick={handleBack} className="text-xs text-gray-600 hover:text-gray-400 transition-colors" data-id="element-4059">
                  Back
                </button>}
            </div>

            {/* Progress Bar - Subtle lines */}
            <div className="flex gap-1" data-id="element-4060">
              {Array.from({
              length: totalQuestions
            }).map((_, i) => <div key={i} className={`h-0.5 flex-1 transition-colors duration-300 ${i <= currentQuestion ? 'bg-[#64748B]' : 'bg-[#2B2B2B]'}`} data-id="element-4061" />)}
            </div>

            {/* Helper Text */}
            <p className="text-xs text-gray-600 mt-4" data-id="element-4062">
              There are no right answers. Choose what you usually do.
            </p>
          </div>
        </div>

        {/* CENTER - Question & Options */}
        <div className="flex-1 flex items-center justify-center px-6 pt-32 pb-12" data-id="element-4063">
          <div className="max-w-3xl w-full space-y-12" data-id="element-4064">
            {/* Question Text */}
            <h1 className="text-2xl md:text-3xl font-light text-[#F5F5F5] leading-relaxed text-center" data-id="element-4065">
              {question.text}
            </h1>

            {/* Answer Options */}
            <div className="space-y-4" data-id="element-4066">
              {question.options.map((option, index) => <button key={index} onClick={() => handleAnswer(index)} className="w-full p-6 border border-[#2B2B2B] bg-[#0A0A0A] text-left text-base text-gray-400 leading-relaxed hover:border-[#64748B] hover:text-[#F5F5F5] transition-all duration-200 group" data-id="element-4067">
                  <span className="block" data-id="element-4068">{option}</span>
                </button>)}
            </div>
          </div>
        </div>
      </div>
    </>;
}